import { AlertCircle, Loader2, Mail, Zap } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { UnifiedButton } from '../../components/ui/unified-button'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../stores/authStore'

type AuthStep = 'auth' | 'email-sent'

export default function LoginPage() {
  const navigate = useNavigate()
  const { signIn, signUp, signInWithGoogle, user } = useAuth()

  const [step, setStep] = useState<AuthStep>('auth')
  const [isLogin, setIsLogin] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/')
    }
  }, [user, navigate])

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate passwords match for signup
    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)

    try {
      if (isLogin) {
        await signIn(email, password)
        // Supabase will handle the redirect
      } else {
        await signUp(email, password)
        setStep('email-sent')
      }
    } catch (err) {
      // Use Supabase error messages directly
      const errorMessage = err instanceof Error ? err.message : ''
      if (errorMessage) {
        // Handle specific Supabase errors
        if (errorMessage.includes('User already registered')) {
          setError('An account with this email already exists. Please sign in instead.')
        } else if (errorMessage.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.')
        } else if (errorMessage.includes('Email not confirmed')) {
          setError('Please confirm your email before signing in.')
        } else {
          setError(errorMessage)
        }
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleAuth = async () => {
    setError(null)
    setIsLoading(true)

    try {
      await signInWithGoogle()
      // Browser will redirect to Google
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google')
      setIsLoading(false)
    }
  }

  const resetFlow = () => {
    setStep('auth')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setError(null)
    setIsLogin(false)
    setResendSuccess(false)
  }

  const handleResendEmail = async () => {
    setIsResending(true)
    setResendSuccess(false)

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/email-confirmed`
        }
      })

      if (error) throw error

      setResendSuccess(true)
      // Hide success message after 5 seconds
      setTimeout(() => setResendSuccess(false), 5000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend email')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <div className="flex items-center space-x-2">
            <Zap className="h-8 w-8 text-orange-500" fill="currentColor" />
            <span className="text-2xl font-bold tracking-tight">Loadline</span>
          </div>
        </div>

        {step === 'auth' && (
          <Card className="pt-8 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-center text-2xl leading-[1rem] font-semibold">
                {isLogin ? 'Welcome back' : 'Create your account'}
              </CardTitle>
              <CardDescription className="text-center text-sm">
                {isLogin
                  ? 'Enter your email to sign in to your account'
                  : 'Enter your email below to create your account'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailAuth} className="space-y-4">
                {error && (
                  <div className="text-destructive bg-destructive/10 flex items-start gap-2 rounded-md p-3 text-sm">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-background focus:ring-ring w-full rounded-md border px-3 py-2 focus:border-transparent focus:ring-2 focus:outline-none"
                    placeholder="name@example.com"
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-background focus:ring-ring w-full rounded-md border px-3 py-2 focus:border-transparent focus:ring-2 focus:outline-none"
                    placeholder="••••••••"
                    required
                    disabled={isLoading}
                    minLength={8}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                  />
                  {!isLogin && (
                    <p className="text-muted-foreground text-xs">Must be at least 8 characters</p>
                  )}
                </div>

                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-background focus:ring-ring w-full rounded-md border px-3 py-2 focus:border-transparent focus:ring-2 focus:outline-none"
                      placeholder="••••••••"
                      required
                      disabled={isLoading}
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                )}

                <UnifiedButton type="submit" className="w-full" disabled={isLoading}>
                  <div className="mx-auto flex items-center gap-1">
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isLogin ? 'Signing in...' : 'Creating account...'}
                      </>
                    ) : isLogin ? (
                      'Sign in'
                    ) : (
                      'Create account'
                    )}
                  </div>
                </UnifiedButton>
                {isLogin && (
                  <p className="text-muted-foreground text-center text-sm">
                    Forgot your password?{' '}
                    <a
                      onClick={() => navigate('/reset-password')}
                      className="hover:text-primary cursor-pointer underline underline-offset-4 transition-colors"
                    >
                      Reset it here
                    </a>
                  </p>
                )}

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background text-muted-foreground px-2">OR</span>
                  </div>
                </div>

                <UnifiedButton
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleAuth}
                  disabled={isLoading}
                >
                  <div className="mx-auto flex items-center gap-1">
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continue with Google
                  </div>
                </UnifiedButton>

                <div className="space-y-2">
                  <p className="text-muted-foreground text-center text-sm">
                    {isLogin ? "Don't have an account? " : 'Already have an account? '}
                    <a
                      onClick={() => {
                        setIsLogin(!isLogin)
                        setError(null)
                        setConfirmPassword('')
                      }}
                      className="hover:text-primary cursor-pointer underline underline-offset-4 transition-colors"
                    >
                      {isLogin ? 'Sign up' : 'Sign in'}
                    </a>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {step === 'email-sent' && (
          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-2 text-center">
              <div className="mb-2 flex justify-center">
                <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                  <Mail className="text-primary h-6 w-6" />
                </div>
              </div>
              <CardTitle className="text-xl">Check your email</CardTitle>
              <CardDescription>
                We sent a confirmation link to{' '}
                <span className="text-foreground font-medium">{email}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-center text-sm">
                Click the link in your email to confirm your account. Once confirmed, this page will
                automatically redirect to setup.
              </p>
              <div className="space-y-3">
                {resendSuccess && (
                  <div className="rounded-md bg-green-50 p-3 text-center text-sm text-green-600 dark:bg-green-900/20 dark:text-green-400">
                    Email sent! Check your inbox.
                  </div>
                )}
                <UnifiedButton variant="outline" className="w-full" onClick={resetFlow}>
                  Back to sign up
                </UnifiedButton>
                <div className="flex items-center gap-1">
                  <p className="text-muted-foreground text-center text-xs">
                    Can&apos;t find the email? Check your spam folder or
                  </p>
                  <a
                    onClick={handleResendEmail}
                    className="text-primary hover:text-primary/80 cursor-pointer text-xs underline transition-colors"
                  >
                    {isResending ? 'Sending...' : 'Send again'}
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
